import BaseController from "confinus_ui/base_controller";
import DataTableController from "confinus_ui/tables/data_table_controller";
import { showConfirmModal, showModal } from "lib/overlay/modal";

export default class BulkActionsBarController extends BaseController {
  public static targets = [
    "selectionCount",
    "selectedIdsContainer",
    "bulkActionsBar",
    "dropdownForm",
  ];

  public static values = {
    selectedIdsFieldName: { type: String, default: "selected_ids" },
  };

  declare private readonly selectionCountTarget: HTMLElement;
  declare private readonly selectedIdsContainerTargets: HTMLElement[];
  declare private readonly bulkActionsBarTarget: HTMLElement;
  declare private readonly dropdownFormTargets: HTMLFormElement[];
  declare private readonly selectedIdsFieldNameValue: string;

  private dataTableController: DataTableController | null = null;
  private tableId: string | null = null;
  private offPageSelectionCount: number = 0;

  public setDataTableController(controller: DataTableController) {
    this.dataTableController = controller;
    // Check for existing selections when controller is set
    this.checkForExistingSelections();
  }

  public setTableId(id: string) {
    this.tableId = id;
    // Check for persisted selections when table ID is set
    this.checkForExistingSelections();
  }

  private checkForExistingSelections() {
    // If we have both a table ID and controller, check for persisted selections
    if (this.tableId && this.dataTableController) {
      const selectedIds = this.dataTableController.getSelectedRowIds();
      if (selectedIds.length > 0) {
        this.handleSelectionChange(selectedIds);
      }
    }
  }

  public handleSelectionChange(selectedIds: string[]) {
    const count = selectedIds.length;

    // Calculate off-page selections if persistence is enabled
    if (this.tableId && this.dataTableController) {
      const visibleCount =
        this.dataTableController.getVisibleSelectedRowIds().length;
      this.offPageSelectionCount = count - visibleCount;
    }

    // Update selection count with indicator for off-page selections
    let countText = `${count} ${count === 1 ? "item" : "items"} selected`;
    if (this.offPageSelectionCount > 0) {
      countText += ` (${this.offPageSelectionCount} from other pages)`;
    }
    this.selectionCountTarget.textContent = countText;

    // Update visibility
    if (count > 0) {
      this.element.classList.remove("hidden");
    } else {
      this.element.classList.add("hidden");
    }

    // Update hidden inputs with ALL selected IDs (including from other pages)
    this.updateSelectedIdsInputs(selectedIds);
  }

  private updateSelectedIdsInputs(selectedIds: string[]) {
    // Update each container with hidden inputs
    this.selectedIdsContainerTargets.forEach((container) => {
      const fieldName =
        container.dataset.fieldName || this.selectedIdsFieldNameValue;

      // Clear existing inputs in this container
      container.innerHTML = "";

      // Add new inputs for each selected ID
      selectedIds.forEach((id) => {
        const newInput = document.createElement("input");
        newInput.type = "hidden";
        newInput.name = `${fieldName}[]`;
        newInput.value = id;
        container.appendChild(newInput);
      });
    });
  }

  public clearSelection() {
    if (this.dataTableController) {
      this.dataTableController.clearSelection();
    }
  }

  public async handleBulkAction(event: Event) {
    event.preventDefault();

    const button = event.currentTarget as HTMLElement;
    const form = button.closest("form") as HTMLFormElement;

    if (!form || !this.dataTableController) {
      return;
    }

    if (button.dataset.modal === "true") {
      this.openBulkActionModal(form);
      return;
    }

    await this.confirmAndSubmitForm(form, button.dataset.confirm);
  }

  public async handleDropdownAction(event: Event) {
    event.preventDefault();

    const menuItem = event.currentTarget as HTMLElement;
    const actionName = menuItem.dataset.actionName;

    if (!this.dataTableController || !actionName) {
      return;
    }

    const form = this.findFormByActionName(actionName);
    if (!form) {
      return;
    }

    if (menuItem.dataset.modal === "true") {
      this.openBulkActionModal(form);
      return;
    }

    await this.confirmAndSubmitForm(form, menuItem.dataset.confirm);
  }

  private findFormByActionName(
    actionName: string,
  ): HTMLFormElement | undefined {
    return this.dropdownFormTargets.find((f) => {
      const actionNameField = f.querySelector(
        'input[name="action_name"]',
      ) as HTMLInputElement;
      return actionNameField?.value === actionName;
    });
  }

  private openBulkActionModal(form: HTMLFormElement): void {
    const selectedIds = this.dataTableController!.getSelectedRowIds();
    const url = new URL(form.action, window.location.origin);
    selectedIds.forEach((id) => url.searchParams.append("selected_ids[]", id));

    showModal({
      contentHref: url.toString(),
      closeOnOverlayClick: true,
    });
  }

  private async confirmAndSubmitForm(
    form: HTMLFormElement,
    confirmMessage?: string,
  ): Promise<void> {
    if (confirmMessage && this.dataTableController) {
      const count = this.dataTableController.getSelectionCount();
      const message = this.interpolateCount(confirmMessage, count);

      const confirmed = await this.showConfirmModal(message);
      if (!confirmed) {
        return;
      }
    }

    // Clear selections immediately after confirmation to provide visual feedback
    this.clearSelection();

    // Submit the form which will reload the page
    form.submit();
  }

  private interpolateCount(message: string, count: number): string {
    return message
      .replace(/\{count\}/g, count.toString())
      .replace(/\{\{count\}\}/g, count.toString());
  }

  private async showConfirmModal(message: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      showConfirmModal({
        title: "Are you sure?",
        message: message,
        positiveButtonLabel: "OK",
        positiveButtonReason: "confirm",
        negativeButtonLabel: "Cancel",
        negativeButtonReason: "cancel",
        closeOnOverlayClick: false,
        onClosing: (evt) => {
          const closeReason = evt.detail?.closeReason;
          resolve(closeReason === "confirm");
        },
      });
    });
  }
}
